import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Heart, ShoppingBag } from 'lucide-react';
import Card from '../common/Card';
import Badge from '../common/Badge';
import ImageWithFallback from '../common/ImageWithFallback';
import RatingStars from '../catalog/RatingStars';
import { useStoreContext } from '../../contexts/StoreContext';
import { useCart } from '../../hooks/useCart';
import { useToast } from '../../contexts/ToastContext';
import { formatPrice, hasResellerPricing } from '../../utils/priceCalculator';

// Card de produto enxuto, focado em e-commerce. Usado tanto em carrosséis
// quanto em grids de catálogo.
//
// Diferenciação por papel (alinhado com Doc Funcional v1.0):
// - CONSUMER/guest: vê preço cheio, rating, quick-add.
// - RESELLER em store RESELLER: vê preço cheio (sem riscar) + label
//   "Pague apenas: R$ X"; sem quick-add (precisa entrar no detalhe pra
//   escolher variação e quantidade).
// - MANAGER/REVITER: tratado como CONSUMER aqui (são raros no storefront).

const FAV_KEY = 'reviterFavorites';

function readFavorites() {
  try { return new Set(JSON.parse(localStorage.getItem(FAV_KEY) || '[]')); }
  catch { return new Set(); }
}

function writeFavorites(set) {
  localStorage.setItem(FAV_KEY, JSON.stringify(Array.from(set)));
}

const ProductCard = ({ product }) => {
  const { store } = useStoreContext();
  const { addItem } = useCart();
  const { success, error } = useToast();
  const [isFav, setIsFav] = useState(() => readFavorites().has(product.id));

  const firstVariation = product.variations?.[0];
  const mainImage = product.images?.[0] || product.image || null;

  // Re-sincroniza com mudanças de outras abas (storage event).
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === FAV_KEY) setIsFav(readFavorites().has(product.id));
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [product.id]);

  if (!firstVariation) return null;

  const price = parseFloat(firstVariation.price || 0);
  const resellerPrice = firstVariation.resellerPrice != null
    ? parseFloat(firstVariation.resellerPrice)
    : null;
  // Backend só envia resellerPrice quando user é RESELLER em store reseller
  // (serializeProductForRole). Por isso é OK usar como gate de UI.
  const hasResellerView = hasResellerPricing(firstVariation);

  const productHref = store?.slug
    ? `/fornecedor/${store.slug}/produto/${product.id}`
    : `/produto/${product.id}`;

  const toggleFav = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const set = readFavorites();
    if (set.has(product.id)) set.delete(product.id);
    else set.add(product.id);
    writeFavorites(set);
    setIsFav(set.has(product.id));
  };

  const quickAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (product.variations.length > 1) {
      // Mais de uma variação — usuário precisa escolher cor/tamanho.
      window.location.assign(productHref);
      return;
    }
    try {
      addItem(
        { ...product, supplierId: product.storeId },
        firstVariation,
        1,
        { isReseller: hasResellerView }
      );
      success(`${product.name} adicionado à sacola`);
    } catch (err) {
      error(err.message || 'Não foi possível adicionar');
    }
  };

  return (
    <Card className="h-full flex flex-col group overflow-hidden border border-app-border hover:border-primary/40 hover:shadow-lg transition-all duration-200 bg-white">
      <Link to={productHref} className="flex flex-col h-full">
        <div className="relative w-full aspect-square overflow-hidden bg-app-surface">
          <ImageWithFallback
            src={mainImage}
            alt={product.name}
            fallbackText={product.name}
            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-300"
            loading="lazy"
          />

          {product.variations.length > 1 && (
            <div className="absolute bottom-2 left-2">
              <Badge color="neutral" variant="soft" size="sm" className="!bg-white/90">
                {product.variations.length} variações
              </Badge>
            </div>
          )}

          <button
            type="button"
            onClick={toggleFav}
            className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow transition-colors"
            aria-label={isFav ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
            title={isFav ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
          >
            <Heart
              size={16}
              className={isFav ? 'text-error fill-error' : 'text-app-text-secondary'}
              fill={isFav ? 'currentColor' : 'none'}
            />
          </button>
        </div>

        <div className="p-4 flex flex-col flex-1">
          <h3 className="font-medium text-sm text-app-text mb-1.5 line-clamp-2 min-h-[2.5rem] group-hover:text-primary transition-colors">
            {product.name}
          </h3>

          <RatingStars
            rating={product.averageRating}
            count={product.ratingsCount}
            size={12}
          />

          <div className="mt-auto pt-3 space-y-1">
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-lg sm:text-xl font-bold text-app-text leading-none">
                {formatPrice(price)}
              </span>
              {!hasResellerView && (
                <button
                  type="button"
                  onClick={quickAdd}
                  className="shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-md bg-primary text-white hover:bg-primary-dark transition-colors shadow-sm"
                  aria-label="Adicionar à sacola"
                  title="Adicionar à sacola"
                >
                  <ShoppingBag size={14} />
                </button>
              )}
            </div>
            {hasResellerView && (
              <p className="text-sm font-semibold text-success leading-tight">
                Pague apenas: {formatPrice(resellerPrice)}
              </p>
            )}
          </div>
        </div>
      </Link>
    </Card>
  );
};

export default ProductCard;
